import type { EditorialToutWithImageBlock } from '@/@types/content';
import Image from '@/components/cms/Image';
import RichText from '@/components/cms/RichText';
import Text from '@/components/cms/Text';
import Button from '@/components/ui/Buttons/Button';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { isEditOrPreviewMode } from '@/lib/editModeHelpers';
import { getAddToCartCTALabel, isExternalUrl } from '@/utils/helpers';
import classnames from 'classnames';
import Block from '../cms/Block';
import VimeoVideo from '../ui/VimeoVimeo';
import ResponsiveImage from '../ui/ResponsiveImage';
import type { RequestAQuoteModalBlock as RequestAQuoteModalBlockType } from '@/@types/content';
import RequestAQuoteModalBlock from '@/components/blocks/RequestAQuoteModalBlock';
import VisibilityObserver from '../ui/VisibilityObserver';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { trackSelectEditorialEvent, trackViewEditorialEvent } from '@/analytics/editorialTout';
import { IContent, LinkItem } from '@/@types/cms';
import { useAuth } from 'react-oidc-context';
import { getOIDCState } from '@/auth/oidcConfig';
import Icon from '../ui/Icon';
import { useAddToCart } from '@/hooks/shared/useAddToCart';
import useCatalogLabels from '@/hooks/PDP/useCatalogLabels';
import { AnalyticsContext } from '@/analytics/constants';
import { ItemListAnalyticsModel } from '@/@types/client-api';
import { getProductCode, trackSelectPromotionEvent } from '@/analytics/ecommerce';
import { useFetchAnalyticsItems } from '@/analytics/useFetchAnalyticsItems';

enum Layout {
  MediaLeft = 'media-left',
  MediaRight = 'media-right'
}

enum ImagePlacement {
  AboveText = 'above-text',
  BelowText = 'below-text'
}

enum LinkStyle {
  Primary = 'primary',
  Secondary = 'secondary'
}

type EditorialLink = {
  link?: LinkItem;
  linkStyle?: LinkStyle;
  renderAsAddToCart?: boolean;
  productToAddToCart?: IContent;
  propertyName?: string;
};

interface EditorialToutWithImageBlockProps extends EditorialToutWithImageBlock {
  smallImage: boolean;
  outerHeading?: string;
  onPrimaryButtonClick?: () => void;
  eyebrowSize?: 'normal' | 'small';
  context?: AnalyticsContext;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
  onPromotionVisible?: (promotionId?: string) => void;
}

const EditorialToutWithImageBlock: React.FC<EditorialToutWithImageBlockProps> = ({
  contentLink,
  layout = Layout.MediaLeft,
  image,
  roundedCorners = false,
  videoID,
  logo,
  eyebrowText,
  eyebrowIcon,
  eyebrowSize = 'small',
  gradientHeading = false,
  heading,
  copy,
  primaryLink,
  link1Style,
  link1RenderAsAddToCart,
  product1ToAddToCart,
  secondaryLink,
  link2Style,
  link2RenderAsAddToCart,
  product2ToAddToCart,
  checkListRed = false,
  smallImage = false,
  aspectRadioLayout = '1:1',
  grayBackground = false,
  outerHeading,
  requestQuoteFlowtComponent,
  imagePlacement = ImagePlacement.AboveText,
  contentType,
  requireAuthOnLinks,
  link1RenderAsButton,
  context,
  name,
  itemListAnalyticsModel,
  onPromotionVisible,
  onPrimaryButtonClick
}) => {
  const auth = useAuth();

  const promoEvent = useMemo(
    () => ({
      creative_name: image?.name?.toString() ?? '',
      creative_slot: contentType?.at(-1) ?? '',
      promotion_id: contentLink?.id?.toString() ?? '',
      promotion_name: name ?? '',
      context: context
    }),
    [contentLink?.id, contentType, context, image?.name, name]
  );

  const { addToCart } = useAddToCart(context, undefined, itemListAnalyticsModel);
  const { getAnalyticItem } = useFetchAnalyticsItems();
  const hasAddToCartLink = !!link1RenderAsAddToCart || !!link2RenderAsAddToCart;
  const { otherLabels, isLoading: isLoadingLabels } = useCatalogLabels(!hasAddToCartLink);
  const editMode = isEditOrPreviewMode();
  const screenSizeXsOnly = useMediaQuery(ScreenSizeQueries.xsOnly);
  const requestAQuoteModalBlockProps =
    requestQuoteFlowtComponent?.[0] as RequestAQuoteModalBlockType;

  const links: Record<number, EditorialLink> = useMemo(() => {
    return {
      [1]: {
        link: primaryLink as LinkItem,
        linkStyle: link1Style as LinkStyle,
        renderAsAddToCart: !!link1RenderAsAddToCart,
        productToAddToCart: product1ToAddToCart?.[0],
        propertyName: 'PrimaryLink'
      },
      [2]: {
        link: secondaryLink as LinkItem,
        linkStyle: link2Style as LinkStyle,
        renderAsAddToCart: !!link2RenderAsAddToCart,
        productToAddToCart: product2ToAddToCart?.[0],
        propertyName: 'SecondaryLink'
      }
    };
  }, [
    link1RenderAsAddToCart,
    link1Style,
    link2RenderAsAddToCart,
    link2Style,
    primaryLink,
    product1ToAddToCart,
    product2ToAddToCart,
    secondaryLink
  ]);

  // TODO: Temporary flag, update when a ticket is created to handle alternative layout
  const alternativeLayout = false;

  const flexClasses = classnames(
    'flex flex-col items-center gap-4',
    {
      'sm:gap-12': !alternativeLayout,
      'sm:grid sm:min-h-[360px] sm:grid-cols-2 sm:gap-6': alternativeLayout,
      'md:gap-10': !alternativeLayout,
      'lg:gap-[133px]': !alternativeLayout
    },
    {
      'md:flex-row': layout === Layout.MediaLeft,
      'md:flex-row-reverse': layout === Layout.MediaRight
    },
    {
      'flex-col-reverse': imagePlacement === ImagePlacement.BelowText
    },
    {
      'overflow-hidden rounded-lg': alternativeLayout
    },
    {
      'bg-gray-feather': grayBackground
    }
  );

  const smallImageClasses = classnames('w-1/2 self-start', 'md:w-2/5');

  const imageColumnClasses = classnames('w-full', {
    'lg:w-1/2 lg:flex-[0_0_calc(50%-12px)]': !alternativeLayout,
    'sm:featured-content-image sm:h-full': alternativeLayout,
    'sm:order-1': alternativeLayout && layout === Layout.MediaRight
  });

  const infoColumnClasses = classnames('flex w-full flex-col gap-8', {
    'p-8': alternativeLayout,
    'lg:p-16': alternativeLayout,
    'lg:gap-8': !smallImage,
    'pl-16': !!grayBackground
  });

  const infoContentClasses = classnames('flex flex-col items-start gap-6', 'md:gap-4', {
    'lg:gap-6': !alternativeLayout,
    'lg:max-w-[394px]': alternativeLayout,
    '!gap-4': smallImage
  });

  const stringToAspectRatio = (aspectRatio: string) => {
    const isValidAspectRatio = /^(\d+):(\d+)$/.test(aspectRatio);
    if (!isValidAspectRatio) {
      return 1;
    }

    const [width, height] = aspectRatio.split(':').map(n => parseInt(n, 10));
    return width / height;
  };

  const imageAspectRatio = stringToAspectRatio(aspectRadioLayout);

  const linkColor = (style?: string) => {
    switch (style) {
      case LinkStyle.Primary:
        return 'black';
      case LinkStyle.Secondary:
        return 'outline-black';
      default:
        return 'outline-black';
    }
  };

  const [isVisible, setIsVisible] = useState(false);

  const handleOnVisible = useCallback(() => {
    setIsVisible(true);
    onPromotionVisible?.(contentLink?.id?.toString());
  }, [contentLink?.id, onPromotionVisible]);

  useEffect(() => {
    if (isVisible) {
      const nonPromo = {
        block_name: heading ?? '',
        block_type: contentType?.[1] ?? '',
        block_id: contentLink?.id?.toString() ?? ''
      };
      trackViewEditorialEvent(nonPromo);
    }
  }, [contentLink?.id, contentType, heading, isVisible]);

  const addToCartCTALabel = useCallback(
    (linkNumber: number) => {
      const link = links[linkNumber];
      const text = link?.link?.text;
      const productItem = link?.productToAddToCart;

      if (productItem) {
        // If this is from a PDP page with expanded content in the contentLink
        if (
          'contentLink' in productItem &&
          productItem.contentLink?.expanded &&
          typeof productItem.contentLink.expanded === 'object'
        ) {
          return getAddToCartCTALabel(
            productItem.contentLink.expanded,
            text ?? 'Add to cart',
            otherLabels
          );
        }

        // Generic page case - use the product item directly
        return getAddToCartCTALabel(productItem, text ?? 'Add to cart', otherLabels);
      }
      return text ?? 'Add to cart';
    },
    [links, otherLabels]
  );

  const trackSelectPromotion = useCallback(
    async (productItem?: IContent) => {
      if (productItem) {
        try {
          const code = getProductCode(productItem);
          if (!code) return;

          const itemAnalyticsModel = await getAnalyticItem(code);
          trackSelectPromotionEvent(promoEvent, itemAnalyticsModel, itemListAnalyticsModel);
        } catch {
          // Add-to-cart should remain available when analytics cannot be loaded.
        }
      }
    },
    [getAnalyticItem, itemListAnalyticsModel, promoEvent]
  );

  const handleAddToCart = useCallback(
    async (productItem: IContent) => {
      const code = getProductCode(productItem);
      if (!code) return;

      addToCart({ code });

      await trackSelectPromotion(productItem);
    },
    [addToCart, trackSelectPromotion]
  );

  const handleLinkClick = useCallback(
    async (linkNumber: number, e?: React.MouseEvent) => {
      const l = links[linkNumber];
      const nonPromo = {
        block_name: l.link?.title || '',
        block_type: 'LinkItem',
        block_id: l.link?.contentLink?.id?.toString()
      };
      trackSelectEditorialEvent(nonPromo);
      if (onPrimaryButtonClick) {
        e?.preventDefault();
        onPrimaryButtonClick();
      }

      if (l.linkStyle == LinkStyle.Secondary && !l.renderAsAddToCart && l.productToAddToCart) {
        await trackSelectPromotion(l.productToAddToCart);
      }

      if (l.renderAsAddToCart && l.productToAddToCart && getProductCode(l.productToAddToCart)) {
        void handleAddToCart(l.productToAddToCart);
      }
    },
    [links, onPrimaryButtonClick, trackSelectPromotion, handleAddToCart]
  );

  const handleRequestQuoteClick = () => {
    const nonPromo = {
      block_name: requestAQuoteModalBlockProps?.name ?? '',
      block_type: requestAQuoteModalBlockProps?.contentType?.[1] ?? '',
      block_id: requestAQuoteModalBlockProps?.contentLink?.id?.toString() ?? ''
    };
    trackSelectEditorialEvent(nonPromo);
  };

  const renderLink = useCallback(
    (linkNumber: number) => {
      const linkItem = links[linkNumber];
      if (!linkItem) return null;

      const link = linkItem.link;
      if (!link && !editMode) {
        return null;
      }

      const classNames = '!w-auto !justify-center';
      const { isAuthenticated, user, signinRedirect } = auth;
      if (requireAuthOnLinks && !isAuthenticated) {
        return (
          <Button
            className={classNames}
            propertyName={linkItem.propertyName}
            color={linkColor(linkItem.linkStyle)}
            label={link!.text}
            title={link!.title}
            size="large"
            onClick={() => {
              signinRedirect({ state: getOIDCState() });
            }}
            iconRight={
              link!.target === '_blank' || isExternalUrl(link!.href ?? '/') ? 'link-out' : undefined
            }
          />
        );
      }

      const href = requireAuthOnLinks
        ? link!.href?.replace('{individualSk}', user?.profile?.IndividualSK as string)
        : link!.href;
      const canAddToCart =
        !!linkItem.renderAsAddToCart && !!getProductCode(linkItem.productToAddToCart);
      return (
        <Button
          className={classNames}
          propertyName={linkItem.propertyName}
          color={linkColor(linkItem!.linkStyle)}
          href={(!!link1RenderAsButton && linkNumber === 1) || canAddToCart ? undefined : href}
          label={canAddToCart ? addToCartCTALabel(linkNumber) : link!.text}
          loading={canAddToCart && isLoadingLabels}
          target={link!.target}
          title={link!.title}
          iconRight={
            (link!.target === '_blank' || isExternalUrl(link!.href ?? '/')) && !link1RenderAsButton
              ? 'link-out'
              : undefined
          }
          size="large"
          onClick={async (e: React.MouseEvent<Element, MouseEvent>) =>
            await handleLinkClick(linkNumber, e)
          }
        />
      );
    },
    [
      links,
      editMode,
      auth,
      requireAuthOnLinks,
      link1RenderAsButton,
      addToCartCTALabel,
      isLoadingLabels,
      handleLinkClick
    ]
  );

  return (
    <div className="relative">
      <VisibilityObserver onVisible={handleOnVisible}>
        <Block
          contentLinkID={contentLink?.id}
          className={classnames(
            'container module-spacing flex flex-col gap-8 overflow-x-clip bg-white',
            {
              'border-b border-solid border-gray-light px-0': smallImage
            }
          )}
        >
          {outerHeading && (
            <Text propertyName="OuterHeading" element="h3" className="heading-6-medium">
              {outerHeading}
            </Text>
          )}
          <div className={flexClasses}>
            {image || videoID || editMode ? (
              <div className={smallImage ? smallImageClasses : imageColumnClasses}>
                {(!videoID || editMode) && (
                  <ResponsiveImage
                    propertyName="Image"
                    src={image?.expanded?.url ?? image?.url}
                    alt={image?.alt as string}
                    className={classnames('size-full object-cover', {
                      'aspect-square': !videoID,
                      'aspect-video': videoID,
                      'rounded-lg': roundedCorners
                    })}
                    loading="lazy"
                    aspectRatio={imageAspectRatio}
                    imageSizes={{
                      xs: '330px',
                      sm: '500px',
                      md: '768px',
                      lg: '638px'
                    }}
                  />
                )}

                {!editMode && videoID && (
                  <VimeoVideo
                    videoId={videoID}
                    thumbnail={image}
                    title={image?.alt as string}
                    ratio={16 / 9}
                  />
                )}

                {editMode && (
                  <Text propertyName="videoID" className="mt-2">
                    {videoID ? (
                      `Vimeo video ID: ${videoID}`
                    ) : (
                      <span className="font-bold">Add Vimeo video ID</span>
                    )}
                  </Text>
                )}
              </div>
            ) : null}

            <div className={infoColumnClasses}>
              <div className={infoContentClasses}>
                <Image
                  propertyName="Logo"
                  src={logo?.url}
                  alt={logo?.alt as string}
                  className={classnames('h-5')}
                />

                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-start">
                    {!!eyebrowIcon && <Icon size="small" name={eyebrowIcon} className="mr-1" />}
                    <Text
                      propertyName="EyebrowText"
                      element="span"
                      className={
                        eyebrowSize == 'small'
                          ? classnames('text-gray-dark', {
                              'text-sm font-medium': imageAspectRatio === 1,
                              'text-xs': imageAspectRatio !== 1
                            })
                          : classnames({
                              'heading-6-medium': imageAspectRatio === 1,
                              'text-xs text-gray-dark': imageAspectRatio !== 1
                            })
                      }
                    >
                      {eyebrowText}
                    </Text>
                  </div>

                  <Text
                    propertyName="Heading"
                    element="h2"
                    className={classnames({
                      'bg-gradient bg-clip-text leading-7 text-transparent': !!gradientHeading,
                      'heading-3 mt-1': smallImage && !screenSizeXsOnly,
                      'heading-5 mt-1': !(smallImage && !screenSizeXsOnly)
                    })}
                  >
                    {heading}
                  </Text>
                </div>

                <RichText
                  propertyName="Copy"
                  content={copy || ''}
                  className="text-1 text-gray-dark"
                  checkList={true}
                  checklistRed={checkListRed}
                />
              </div>

              {primaryLink || secondaryLink || editMode || requestAQuoteModalBlockProps ? (
                <div className="flex w-full flex-col items-stretch gap-2 sm:w-fit">
                  {renderLink(1)}
                  {renderLink(2)}
                  {requestAQuoteModalBlockProps ? (
                    <RequestAQuoteModalBlock
                      {...requestAQuoteModalBlockProps}
                      triggerButton={
                        <Button
                          className="w-full !justify-center sm:!w-auto"
                          color={linkColor(link2Style)}
                          label={requestAQuoteModalBlockProps?.heading || ''}
                          size="large"
                          onClick={handleRequestQuoteClick}
                        />
                      }
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </Block>
      </VisibilityObserver>
    </div>
  );
};

export default EditorialToutWithImageBlock;
